import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import { CacheService } from '../../services/cacheService';
import { Trash2, Loader2, CheckCircle, XCircle, Clock, Search, Plus, X, Save, AlertCircle, Edit2, Globe, Mail } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { AdminRoadmapForm } from '../admin/AdminRoadmapForm';
import { AdminInbox } from '../admin/AdminInbox';
import { getAdminRoadmaps, createAdminRoadmap, deleteAdminRoadmap } from '../../services/adminRoadmapService';
import skillsData from '../../constants/skills.json';

const ADMIN_EMAIL = 'dynamicnabid@gmail.com';
const ITEMS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

interface Skill {
  name: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced' | 'hard';
  marketValue: 'low' | 'medium' | 'high' | 'very_high' | 'essential';
  trending: boolean;
  confidenceIndicator: number;
  why: string;
}

interface SiteCustomization {
  siteName: string;
  siteDescription: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  seoKeywords: string;
  seoTitle: string;
  seoDescription: string;
  siteAuthor: string;
  siteEmail: string;
  contactPhone?: string;
  socialLinks?: {
    twitter?: string;
    linkedin?: string;
    github?: string;
    discord?: string;
  };
  customTexts?: {
    [key: string]: string;
  };
}

interface AdminPanelProps {
  user: any;
  onRefreshSubscription?: () => void;
  theme?: 'light' | 'dark';
}

const AdminPanel: React.FC<AdminPanelProps> = ({ user, onRefreshSubscription, theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const [roadmaps, setRoadmaps] = useState<any[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [skills, setSkills] = useState<{[key: string]: any}>({});
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [tab, setTab] = useState<'roadmaps' | 'articles' | 'users' | 'requests' | 'skills' | 'premium-roadmaps' | 'customization' | 'inbox'>('roadmaps');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPremiumRoadmapForm, setShowPremiumRoadmapForm] = useState(false);
  const [premiumRoadmapLoading, setPremiumRoadmapLoading] = useState(false);
  const [premiumRoadmaps, setPremiumRoadmaps] = useState<any[]>([]);
  const [premiumRoadmapsLoading, setPremiumRoadmapsLoading] = useState(false);
  const [deletingPremiumRoadmap, setDeletingPremiumRoadmap] = useState<string | null>(null);
  const [editingPremiumRoadmap, setEditingPremiumRoadmap] = useState<any | null>(null);
  const [publishingPremiumRoadmap, setPublishingPremiumRoadmap] = useState<string | null>(null);
  
  // Site Customization States
  const [customization, setCustomization] = useState<SiteCustomization>({
    siteName: 'NanoLez',
    siteDescription: 'Build your identity through micro-learning',
    primaryColor: '#dc2626',
    secondaryColor: '#f97316',
    accentColor: '#06b6d4',
    seoKeywords: 'learning, skills, identity, micro-learning',
    seoTitle: 'NanoLez - Build Your Identity',
    seoDescription: 'Learn new skills and build your identity with micro-learning pathways',
    siteAuthor: 'NanoLez Team',
    siteEmail: 'dynamicnabid@gmail.com',
    contactPhone: '',
    socialLinks: {
      twitter: '',
      linkedin: '',
      github: '',
      discord: ''
    },
    customTexts: {}
  });
  const [customizationSaving, setCustomizationSaving] = useState(false);
  
  // Pagination states
  const [userPage, setUserPage] = useState(1);
  const [requestPage, setRequestPage] = useState(1);
  
  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Skills Management States
  const [showAddSkillModal, setShowAddSkillModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [newSkill, setNewSkill] = useState<Skill>({
    name: '',
    difficulty: 'beginner',
    marketValue: 'high',
    trending: false,
    confidenceIndicator: 7,
    why: ''
  });
  const [skillSearch, setSkillSearch] = useState('');
  
  const isAdmin = user?.email === ADMIN_EMAIL;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setUserPage(1); // Reset pagination on search
    }, SEARCH_DEBOUNCE_MS);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data only once when component mounts
  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    const fetchAll = async () => {
      try {
        // Fetch only current tab data initially for better performance
        const { data: roadmapsData } = await supabase.from('roadmaps').select('*').limit(ITEMS_PER_PAGE);
        const { data: articlesData } = await supabase.from('articles').select('*').limit(ITEMS_PER_PAGE);
        const { data: usersData } = await supabase.from('user_profiles').select('*').order('created_at', { ascending: false }).limit(ITEMS_PER_PAGE);
        const { data: requestsData } = await supabase.from('subscription_requests').select('*').order('created_at', { ascending: false }).limit(ITEMS_PER_PAGE);
        
        setRoadmaps(roadmapsData || []);
        setArticles(articlesData || []);
        setUsers(usersData || []);
        setRequests(requestsData || []);
        
        // Load skills from JSON
        setSkills(skillsData.categories || {});
        
        // Load site customization from localStorage or database
        const customizationData = localStorage.getItem('siteCustomization');
        if (customizationData) {
          try {
            setCustomization(prev => ({ ...prev, ...JSON.parse(customizationData) }));
          } catch (e) {
            console.error('Error loading customization:', e);
          }
        }
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [isAdmin]);

  // Load premium roadmaps when tab is selected
  useEffect(() => {
    if (tab === 'premium-roadmaps') {
      loadPremiumRoadmaps();
    }
  }, [tab]);

  const loadPremiumRoadmaps = async () => {
    setPremiumRoadmapsLoading(true);
    try {
      const { data, error } = await getAdminRoadmaps(true); // true = get all (admin view)
      if (error) throw error;
      setPremiumRoadmaps(data || []);
    } catch (err) {
      console.error('Error loading premium roadmaps:', err);
    } finally {
      setPremiumRoadmapsLoading(false);
    }
  };

  const saveCustomization = useCallback(async () => {
    setCustomizationSaving(true);
    try {
      // Save to localStorage
      localStorage.setItem('siteCustomization', JSON.stringify(customization));
      
      // Optionally save to database
      try {
        await supabase.from('site_settings').upsert(
          { id: 'customization', settings: customization },
          { onConflict: 'id' }
        );
      } catch (dbError) {
        // If table doesn't exist, just use localStorage
        console.log('Using localStorage for customization', dbError);
      }
      
      alert('✅ Site customization saved successfully!');
    } catch (error) {
      console.error('Error saving customization:', error);
      alert('❌ Error saving customization');
    } finally {
      setCustomizationSaving(false);
    }
  }, [customization]);

  const handleDelete = useCallback(async (type: 'roadmap' | 'article', id: string) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    setDeleting(id);
    try {
      if (type === 'roadmap') {
        await supabase.from('roadmaps').delete().eq('id', id);
        setRoadmaps(r => r.filter(rm => rm.id !== id));
      } else {
        await supabase.from('articles').delete().eq('id', id);
        setArticles(a => a.filter(ar => ar.id !== id));
      }
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error deleting item');
    } finally {
      setDeleting(null);
    }
  }, []);

  const toggleProStatus = useCallback(async (email: string, currentStatus: boolean, planType: 'monthly' | 'yearly' = 'monthly') => {
    setUpdating(email);
    const newStatus = !currentStatus;
    
    // Calculate expiry
    const expiryDate = new Date();
    if (planType === 'yearly') {
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    } else {
      expiryDate.setDate(expiryDate.getDate() + 30);
    }

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ 
          is_pro: newStatus,
          pro_expires_at: newStatus ? expiryDate.toISOString() : null 
        })
        .eq('email', email);

      if (!error) {
        console.log(`✅ User ${email} Pro status toggled to ${newStatus}, cache invalidated`);
        // 🔑 Invalidate cache so user sees update immediately
        CacheService.invalidateCategory('subscription');
        setUsers(prev => prev.map(u => u.email === email ? { 
          ...u, 
          is_pro: newStatus, 
          pro_expires_at: newStatus ? expiryDate.toISOString() : null 
        } : u));
      } else {
        alert('Error updating user status: ' + error.message);
      }
    } catch (error) {
      console.error('Error toggling pro status:', error);
      alert('Error updating user status');
    } finally {
      setUpdating(null);
    }
  }, []);

  const handleRequestAction = async (requestId: string, action: 'approved' | 'rejected', email: string, planType: string) => {
    setUpdating(requestId);
    
    try {
      // 1. Update request status
      const { error: reqError } = await supabase
        .from('subscription_requests')
        .update({ status: action, updated_at: new Date().toISOString() })
        .eq('id', requestId);

      if (reqError) {
        alert('❌ Error updating request: ' + reqError.message);
        setUpdating(null);
        return;
      }

      console.log(`✅ Request ${requestId} status updated to ${action}`);

      // 2. If approved, upgrade user
      if (action === 'approved') {
        const expiryDate = new Date();
        if (planType === 'yearly') {
          expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        } else {
          expiryDate.setDate(expiryDate.getDate() + 30);
        }

        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ 
            is_pro: true,
            pro_expires_at: expiryDate.toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('email', email);
        
        if (updateError) {
          alert('❌ Error upgrading user: ' + updateError.message);
          setUpdating(null);
          return;
        }

        console.log(`✅ User ${email} upgraded to Pro (${planType}), expires: ${expiryDate.toISOString()}`);
        
        // 🔑 KEY FIX: Invalidate cache so user sees update immediately
        CacheService.invalidateCategory('subscription');
      }

      // 3. Refresh local state immediately
      const updatedRequests = requests.map(r => r.id === requestId ? { ...r, status: action, updated_at: new Date().toISOString() } : r);
      setRequests(updatedRequests);

      const expiryDate = action === 'approved' ? new Date() : null;
      if (expiryDate) {
        if (planType === 'yearly') {
          expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        } else {
          expiryDate.setDate(expiryDate.getDate() + 30);
        }
      }

      const updatedUsers = users.map(u => 
        u.email === email 
          ? { 
              ...u, 
              is_pro: action === 'approved',
              pro_expires_at: action === 'approved' ? expiryDate?.toISOString() : null,
              updated_at: new Date().toISOString()
            } 
          : u
      );
      setUsers(updatedUsers);
      
      // 4. Refresh global state if this is the admin user
      if (action === 'approved' && email === user?.email) {
        console.log('🔄 Refreshing subscription for admin user...');
        onRefreshSubscription?.();
      }
      
      // 5. Success message
      if (action === 'approved') {
        alert(`✅ User ${email} has been upgraded to PRO (${planType})!`);
      } else {
        alert(`✅ Request from ${email} has been rejected.`);
      }
    } catch (err) {
      console.error('❌ Error in handleRequestAction:', err);
      alert('❌ An unexpected error occurred. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  const handleAddSkill = async () => {
    if (!selectedCategory || !newSkill.name.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setUpdating('adding-skill');
      const updatedSkills = { ...skills };
      
      if (!updatedSkills[selectedCategory]) {
        updatedSkills[selectedCategory] = { skills: [] };
      }

      // Check if skill already exists
      const existingSkill = updatedSkills[selectedCategory].skills?.find(
        (s: any) => s.name.toLowerCase() === newSkill.name.toLowerCase()
      );

      if (existingSkill) {
        alert('This skill already exists in this category!');
        setUpdating(null);
        return;
      }

      // Add new skill to the category
      updatedSkills[selectedCategory].skills.push({
        ...newSkill,
        name: newSkill.name.trim()
      });

      // Update local state
      setSkills(updatedSkills);

      // Prepare the updated skills data
      const updatedSkillsData = {
        ...skillsData,
        categories: updatedSkills,
        metadata: {
          ...skillsData.metadata,
          totalSkills: Object.values(updatedSkills).reduce(
            (sum: number, cat: any) => sum + (cat.skills?.length || 0),
            0
          ),
          lastUpdated: new Date().toISOString().split('T')[0]
        }
      };

      // Save to localStorage for persistence during session
      localStorage.setItem('nanolezSkillsDatabase', JSON.stringify(updatedSkillsData));

      alert(`✅ Skill "${newSkill.name}" added to "${selectedCategory}"!`);
      setNewSkill({
        name: '',
        difficulty: 'beginner',
        marketValue: 'high',
        trending: false,
        confidenceIndicator: 7,
        why: ''
      });
      setShowAddSkillModal(false);
      setSelectedCategory('');
    } catch (error) {
      alert(`❌ Error adding skill: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteSkill = async (category: string, skillName: string) => {
    if (!window.confirm(`Delete skill "${skillName}" from "${category}"?`)) return;

    try {
      setDeleting(`${category}|${skillName}`);
      const updatedSkills = { ...skills };

      if (updatedSkills[category]) {
        updatedSkills[category].skills = updatedSkills[category].skills.filter(
          (skill: any) => skill.name !== skillName
        );
      }

      setSkills(updatedSkills);

      // Prepare the updated skills data
      const updatedSkillsData = {
        ...skillsData,
        categories: updatedSkills,
        metadata: {
          ...skillsData.metadata,
          totalSkills: Object.values(updatedSkills).reduce(
            (sum: number, cat: any) => sum + (cat.skills?.length || 0),
            0
          ),
          lastUpdated: new Date().toISOString().split('T')[0]
        }
      };

      // Save to localStorage for persistence during session
      localStorage.setItem('nanolezSkillsDatabase', JSON.stringify(updatedSkillsData));

      alert(`✅ Skill deleted!`);
    } catch (error) {
      alert(`❌ Error deleting skill: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setDeleting(null);
    }
  };


  // Memoized filtered users
  const filteredUsers = useMemo(() => 
    users.filter(u => 
      u.email?.toLowerCase().includes(debouncedSearch.toLowerCase()) || 
      u.display_name?.toLowerCase().includes(debouncedSearch.toLowerCase())
    ),
    [users, debouncedSearch]
  );

  // Paginated users
  const paginatedUsers = useMemo(() => {
    const start = (userPage - 1) * ITEMS_PER_PAGE;
    return filteredUsers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredUsers, userPage]);

  // Paginated requests

  // Handler for premium roadmap creation or editing
  const handlePremiumRoadmapSubmit = async (data: any) => {
    setPremiumRoadmapLoading(true);
    try {
      if (editingPremiumRoadmap) {
        // Edit existing roadmap
        const { updateAdminRoadmap } = await import('../../services/adminRoadmapService');
        const { error } = await updateAdminRoadmap(editingPremiumRoadmap.id, data);
        if (error) throw error;
        alert('Premium Roadmap updated successfully!');
        setEditingPremiumRoadmap(null);
      } else {
        // Create new roadmap
        const { error } = await createAdminRoadmap(user, data);
        if (error) throw error;
        alert('Premium Roadmap created successfully!');
      }
      setShowPremiumRoadmapForm(false);
      loadPremiumRoadmaps(); // Reload the list
    } catch (error) {
      console.error('Error creating/updating premium roadmap:', error);
      alert('Error creating/updating premium roadmap');
    } finally {
      setPremiumRoadmapLoading(false);
    }
  };

  const handleDeletePremiumRoadmap = async (roadmapId: string) => {
    if (!confirm('Are you sure you want to delete this roadmap?')) return;
    
    setDeletingPremiumRoadmap(roadmapId);
    try {
      const { error } = await deleteAdminRoadmap(roadmapId);
      if (error) throw error;
      alert('Premium Roadmap deleted successfully!');
      loadPremiumRoadmaps(); // Reload the list
    } catch (error) {
      console.error('Error deleting premium roadmap:', error);
      alert('Error deleting premium roadmap');
    } finally {
      setDeletingPremiumRoadmap(null);
    }
  };

  const handleEditPremiumRoadmap = (roadmap: any) => {
    setEditingPremiumRoadmap(roadmap);
    setShowPremiumRoadmapForm(true);
  };

  const handlePublishPremiumRoadmap = async (roadmapId: string, currentStatus: boolean) => {
    setPublishingPremiumRoadmap(roadmapId);
    try {
      const { publishAdminRoadmap } = await import('../../services/adminRoadmapService');
      const { error } = await publishAdminRoadmap(roadmapId, !currentStatus);
      if (error) throw error;
      alert(`Roadmap ${!currentStatus ? 'published' : 'unpublished'} successfully!`);
      loadPremiumRoadmaps(); // Reload the list
    } catch (error) {
      console.error('Error publishing/unpublishing roadmap:', error);
      alert('Error updating roadmap status');
    } finally {
      setPublishingPremiumRoadmap(null);
    }
  };

  const paginatedRequests = useMemo(() => {
    const start = (requestPage - 1) * ITEMS_PER_PAGE;
    return requests.slice(start, start + ITEMS_PER_PAGE);
  }, [requests, requestPage]);

  const totalUserPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const totalRequestPages = Math.ceil(requests.length / ITEMS_PER_PAGE);

  if (!isAdmin) {
    return (
      <div className="max-w-6xl mx-auto py-12 px-4 text-center">
        <p className="text-zinc-500">Access Denied. Admin only.</p>
      </div>
    );
  }

  return (
    <div className={`max-w-6xl mx-auto py-12 px-4 ${isAdmin ? 'text-zinc-900 dark:text-zinc-100' : ''}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold text-red-600 dark:text-red-400">Admin Panel</h1>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input 
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 rounded-xl border bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 outline-none focus:border-red-500 transition-all text-sm w-full md:w-64"
          />
        </div>
      </div>

      <div className="mb-8">
        <select
          value={tab}
          onChange={(e) => setTab(e.target.value as any)}
          className="w-full md:w-64 px-4 py-3 rounded-xl border-2 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-bold text-sm focus:border-red-500 focus:outline-none transition-all"
        >
          <option value="roadmaps">📚 Roadmaps ({roadmaps.length})</option>
          <option value="articles">📄 Articles ({articles.length})</option>
          <option value="users">👥 Users ({users.length})</option>
          <option value="requests">⬆️ Upgrade Requests ({requests.filter(r => r.status === 'pending').length} pending)</option>
          <option value="skills">🎯 Skills Database ({Object.values(skills).reduce((sum, cat: any) => sum + (cat.skills?.length || 0), 0)})</option>
          <option value="premium-roadmaps">🏆 Premium Roadmaps</option>
          <option value="inbox">📧 Messages Inbox</option>
          <option value="customization">⚙️ Site Customization</option>
        </select>
      </div>

      {loading ? (
        <LoadingSpinner context="data" message="Loading admin data..." size="lg" />
      ) : (
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          {tab === 'roadmaps' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roadmaps.map(rm => (
                <div key={rm.id} className="p-4 rounded-2xl border bg-white dark:bg-zinc-900 border-zinc-100 dark:border-zinc-800 flex items-center justify-between group">
                  <div className="truncate pr-4">
                    <p className="font-bold text-sm truncate">{rm.title || 'Untitled'}</p>
                    <p className="text-[10px] text-zinc-500">{new Date(rm.created_at).toLocaleDateString()}</p>
                  </div>
                  <button
                    className="p-2 text-zinc-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    onClick={() => handleDelete('roadmap', rm.id)}
                    disabled={deleting === rm.id}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {tab === 'articles' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.length === 0 ? (
                <div className="text-center py-12 text-zinc-500 italic col-span-full">No articles found.</div>
              ) : (
                articles.map(article => (
                  <div key={article.id} className="p-4 rounded-2xl border bg-white dark:bg-zinc-900 border-zinc-100 dark:border-zinc-800 flex items-center justify-between group">
                    <div className="truncate pr-4">
                      <p className="font-bold text-sm truncate">{article.title || 'Untitled'}</p>
                      <p className="text-[10px] text-zinc-500">{new Date(article.created_at).toLocaleDateString()}</p>
                    </div>
                    <button
                      className="p-2 text-zinc-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                      onClick={() => handleDelete('article', article.id)}
                      disabled={deleting === article.id}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === 'users' && (
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-100 dark:border-zinc-800 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-zinc-50 dark:bg-white/5 border-b dark:border-zinc-800">
                      <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">User</th>
                      <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Tier</th>
                      <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Usage</th>
                      <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-zinc-800">
                    {paginatedUsers.map(u => (
                      <tr key={u.email} className="hover:bg-zinc-50 dark:hover:bg-white/5 transition-colors">
                        <td className="py-4 px-6">
                          <p className="text-sm font-bold truncate max-w-[200px]">{u.display_name || 'Anonymous'}</p>
                          <p className="text-xs text-zinc-500 truncate">{u.email}</p>
                        </td>
                        <td className="py-4 px-6">
                          {u.is_pro ? (
                            <div className="flex flex-col gap-1">
                              <span className="inline-flex items-center px-2 py-0.5 bg-amber-500/10 text-amber-600 text-[10px] font-bold rounded-full border border-amber-500/20 w-fit">
                                PRO
                              </span>
                              <span className="text-[9px] text-zinc-400">
                                {u.pro_expires_at ? `Expires: ${new Date(u.pro_expires_at).toLocaleDateString()}` : 'Lifetime'}
                              </span>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 bg-zinc-500/10 text-zinc-500 text-[10px] font-bold rounded-full border border-zinc-500/20">
                              FREE
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <p className="text-xs font-medium">{u.roadmap_generation_count || 0} roadmaps</p>
                        </td>
                        <td className="py-4 px-6">
                          <button
                            onClick={() => toggleProStatus(u.email, !!u.is_pro)}
                            disabled={updating === u.email}
                            className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                              u.is_pro 
                                ? 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400' 
                                : 'bg-red-600 text-white hover:bg-red-700 shadow-sm'
                            } disabled:opacity-50`}
                          >
                            {updating === u.email ? <Loader2 className="w-3 h-3 animate-spin" /> : u.is_pro ? 'Revoke Pro' : 'Make Pro'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalUserPages > 1 && (
                <div className="flex items-center justify-between gap-4 p-4 border-t dark:border-zinc-800 bg-zinc-50 dark:bg-white/5">
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    Showing {(userPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(userPage * ITEMS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setUserPage(p => Math.max(1, p - 1))}
                      disabled={userPage === 1}
                      className="px-3 py-1.5 text-sm rounded-lg bg-zinc-200 dark:bg-zinc-800 disabled:opacity-50 hover:bg-zinc-300 dark:hover:bg-zinc-700"
                    >
                      ← Prev
                    </button>
                    <span className="px-3 py-1.5 text-sm">{userPage} / {totalUserPages}</span>
                    <button
                      onClick={() => setUserPage(p => Math.min(totalUserPages, p + 1))}
                      disabled={userPage === totalUserPages}
                      className="px-3 py-1.5 text-sm rounded-lg bg-zinc-200 dark:bg-zinc-800 disabled:opacity-50 hover:bg-zinc-300 dark:hover:bg-zinc-700"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'requests' && (
            <div className="space-y-4">
              {requests.length === 0 ? (
                <div className="text-center py-12 text-zinc-500 italic">No upgrade requests yet.</div>
              ) : (
                <>
                  {paginatedRequests.map(req => (
                  <div key={req.id} className={`p-6 rounded-2xl border transition-all ${
                    req.status === 'pending' 
                      ? 'bg-white dark:bg-zinc-900 border-red-500/20' 
                      : 'bg-zinc-50/50 dark:bg-zinc-900/50 border-zinc-100 dark:border-zinc-800'
                  }`}>
                    <div className="flex flex-col md:flex-row gap-6 justify-between items-start md:items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-bold">{req.user_email}</p>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-widest ${
                            req.plan_type === 'yearly' 
                              ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' 
                              : 'bg-red-500/10 text-red-500 border-red-500/20'
                          }`}>
                            {req.plan_type} - {req.amount}৳
                          </span>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${
                            req.status === 'pending' ? 'text-amber-500' : 
                            req.status === 'approved' ? 'text-emerald-500' : 'text-red-500'
                          }`}>
                            {req.status === 'pending' && <Clock className="w-3 h-3" />}
                            {req.status === 'approved' && <CheckCircle className="w-3 h-3" />}
                            {req.status === 'rejected' && <XCircle className="w-3 h-3" />}
                            {req.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-xs text-zinc-500">
                          <div className="flex flex-col">
                            <span className="uppercase text-[9px] font-bold tracking-tighter opacity-70">Transaction ID</span>
                            <span className="font-mono">{req.transaction_id || 'N/A'}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="uppercase text-[9px] font-bold tracking-tighter opacity-70">Requested on</span>
                            <span>{new Date(req.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {req.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRequestAction(req.id, 'rejected', req.user_email, req.plan_type)}
                            disabled={!!updating}
                            className="px-4 py-2 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded-xl text-xs font-bold hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all disabled:opacity-50"
                          >
                            Reject
                          </button>
                          <button
                            onClick={() => handleRequestAction(req.id, 'approved', req.user_email, req.plan_type)}
                            disabled={!!updating}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-50 flex items-center gap-2"
                          >
                            {updating === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                            Approve & Upgrade
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                  
                  {/* Pagination */}
                  {totalRequestPages > 1 && (
                    <div className="flex items-center justify-between gap-4 p-4 border-t dark:border-zinc-800 bg-zinc-50 dark:bg-white/5 rounded-xl">
                      <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        Showing {(requestPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(requestPage * ITEMS_PER_PAGE, requests.length)} of {requests.length}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setRequestPage(p => Math.max(1, p - 1))}
                          disabled={requestPage === 1}
                          className="px-3 py-1.5 text-sm rounded-lg bg-zinc-200 dark:bg-zinc-800 disabled:opacity-50 hover:bg-zinc-300 dark:hover:bg-zinc-700"
                        >
                          ← Prev
                        </button>
                        <span className="px-3 py-1.5 text-sm">{requestPage} / {totalRequestPages}</span>
                        <button
                          onClick={() => setRequestPage(p => Math.min(totalRequestPages, p + 1))}
                          disabled={requestPage === totalRequestPages}
                          className="px-3 py-1.5 text-sm rounded-lg bg-zinc-200 dark:bg-zinc-800 disabled:opacity-50 hover:bg-zinc-300 dark:hover:bg-zinc-700"
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === 'skills' && (
            <div className="space-y-4">
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setShowAddSkillModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-xl font-bold text-sm hover:bg-purple-700 transition-all flex items-center gap-2 shadow-lg shadow-purple-500/20"
                >
                  <Plus className="w-4 h-4" /> Add New Skill
                </button>
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input 
                    type="text"
                    placeholder="Search skills..."
                    value={skillSearch}
                    onChange={(e) => setSkillSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-xl border bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 outline-none focus:border-purple-500 transition-all text-sm"
                  />
                </div>
              </div>

              {showAddSkillModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white dark:bg-zinc-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-zinc-200 dark:border-zinc-800">
                    <div className="sticky top-0 flex items-center justify-between p-6 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                      <h2 className="text-2xl font-bold">Add New Skill</h2>
                      <button onClick={() => setShowAddSkillModal(false)} className="text-zinc-400 hover:text-zinc-600">
                        <X className="w-6 h-6" />
                      </button>
                    </div>

                    <div className="p-6 space-y-4">
                      <div>
                        <label className="block text-sm font-bold mb-2">Select Category *</label>
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 outline-none focus:border-purple-500"
                        >
                          <option value="">Choose a category...</option>
                          {Object.keys(skills).map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-bold mb-2">Skill Name *</label>
                        <input
                          type="text"
                          value={newSkill.name}
                          onChange={(e) => setNewSkill({...newSkill, name: e.target.value})}
                          placeholder="e.g., Advanced React Patterns"
                          className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 outline-none focus:border-purple-500"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold mb-2">Difficulty</label>
                          <select
                            value={newSkill.difficulty}
                            onChange={(e) => setNewSkill({...newSkill, difficulty: e.target.value as any})}
                            className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 outline-none focus:border-purple-500"
                          >
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                            <option value="hard">Hard</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-bold mb-2">Market Value</label>
                          <select
                            value={newSkill.marketValue}
                            onChange={(e) => setNewSkill({...newSkill, marketValue: e.target.value as any})}
                            className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 outline-none focus:border-purple-500"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="very_high">Very High</option>
                            <option value="essential">Essential</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold mb-2">Confidence Indicator (1-9)</label>
                          <input
                            type="number"
                            min="1"
                            max="9"
                            value={newSkill.confidenceIndicator}
                            onChange={(e) => setNewSkill({...newSkill, confidenceIndicator: parseInt(e.target.value) || 7})}
                            className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 outline-none focus:border-purple-500"
                          />
                        </div>

                        <div className="flex items-end">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newSkill.trending}
                              onChange={(e) => setNewSkill({...newSkill, trending: e.target.checked})}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm font-bold">Trending</span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-bold mb-2">Why (Psychology Message) *</label>
                        <textarea
                          value={newSkill.why}
                          onChange={(e) => setNewSkill({...newSkill, why: e.target.value})}
                          placeholder="Why should users learn this skill? (e.g., 'Most in-demand framework')"
                          className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 outline-none focus:border-purple-500 h-24 resize-none"
                        />
                      </div>

                      <div className="flex gap-3 pt-4">
                        <button
                          onClick={() => setShowAddSkillModal(false)}
                          className="flex-1 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded-xl font-bold hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddSkill}
                          disabled={updating === 'adding-skill'}
                          className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {updating === 'adding-skill' && <Loader2 className="w-4 h-4 animate-spin" />}
                          Add Skill
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(skills).map(([category, categoryData]: [string, any], index: number) => {
                  const filteredSkills = categoryData.skills?.filter((skill: any) =>
                    skill.name.toLowerCase().includes(skillSearch.toLowerCase())
                  ) || [];

                  if (skillSearch && filteredSkills.length === 0) return null;

                  return (
                    <div key={`${category}-${index}`} className="p-4 rounded-2xl border bg-white dark:bg-zinc-900 border-zinc-100 dark:border-zinc-800">
                      <div className="flex items-center gap-2 mb-4 pb-4 border-b border-zinc-200 dark:border-zinc-800">
                        <span className="text-2xl">{categoryData.icon}</span>
                        <div>
                          <p className="font-bold text-sm">{category}</p>
                          <p className="text-xs text-zinc-500">{categoryData.skills?.length || 0} skills</p>
                        </div>
                      </div>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {categoryData.skills?.map((skill: any) => (
                          <div key={skill.name} className="flex items-start justify-between gap-2 p-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 rounded-lg group transition-colors">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold truncate">{skill.name}</p>
                              <div className="flex gap-1 flex-wrap mt-1">
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium">
                                  {skill.difficulty}
                                </span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium">
                                  {skill.marketValue}
                                </span>
                                {skill.trending && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400 font-medium">
                                    Trending
                                  </span>
                                )}
                              </div>
                              <p className="text-[9px] text-zinc-500 mt-1 line-clamp-2">{skill.why}</p>
                            </div>
                            <button
                              onClick={() => handleDeleteSkill(category, skill.name)}
                              disabled={deleting === `${category}|${skill.name}`}
                              className="p-1 text-zinc-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                            >
                              {deleting === `${category}|${skill.name}` ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Trash2 className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'premium-roadmaps' && !showPremiumRoadmapForm && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Premium Roadmaps (Professional Learning Paths)</h2>
                <button
                  onClick={() => setShowPremiumRoadmapForm(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl font-bold hover:opacity-90 transition-all shadow-lg shadow-orange-500/30"
                >
                  <Plus className="w-5 h-5" />
                  Create Roadmap
                </button>
              </div>
              
              {/* Loading State */}
              {premiumRoadmapsLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
                </div>
              )}
              
              {/* Empty State */}
              {!premiumRoadmapsLoading && premiumRoadmaps.length === 0 && (
                <div className={`text-center py-12 rounded-lg border ${isDark ? 'bg-white/5 border-white/10' : 'bg-zinc-50 border-zinc-200'}`}>
                  <AlertCircle className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`} />
                  <p className={`text-lg font-semibold ${isDark ? 'text-zinc-300' : 'text-zinc-900'}`}>No Premium Roadmaps Yet</p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>
                    Click the button above to create your first premium roadmap
                  </p>
                </div>
              )}
              
              {/* Roadmaps Grid */}
              {!premiumRoadmapsLoading && premiumRoadmaps.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {/* Create New Card */}
                  <button
                    onClick={() => setShowPremiumRoadmapForm(true)}
                    className={`group min-h-[240px] md:min-h-[280px] rounded-3xl border-2 border-dashed flex flex-col items-center justify-center gap-4 transition-all hover:border-yellow-500/60 ${
                      isDark 
                        ? 'border-yellow-500/30 bg-yellow-500/5 hover:bg-yellow-500/10' 
                        : 'border-yellow-300 bg-yellow-50 hover:bg-yellow-100'
                    }`}
                  >
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center shadow-xl shadow-yellow-500/30">
                      <Plus className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-yellow-600 dark:text-yellow-400">Create New</p>
                      <p className={`text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                        Add roadmap
                      </p>
                    </div>
                  </button>
                  
                  {/* Roadmap Cards */}
                  {premiumRoadmaps.map((roadmap) => (
                    <div
                      key={roadmap.id}
                      className={`group relative min-h-[240px] md:min-h-[280px] rounded-3xl p-6 transition-all overflow-hidden ${
                        isDark 
                          ? 'bg-white/[0.03] border border-white/5 hover:bg-white/[0.06]' 
                          : 'bg-white border border-zinc-200/80 hover:border-zinc-300 shadow-sm hover:shadow-xl'
                      }`}
                    >
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 via-orange-500 to-rose-500" />
                      
                      <div className="h-full flex flex-col">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-1 rounded text-[10px] font-semibold uppercase ${
                                roadmap.is_published
                                  ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                                  : isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600'
                              }`}>
                                {roadmap.is_published ? 'Published' : 'Draft'}
                              </span>
                            </div>
                            <h3 className={`text-base font-bold line-clamp-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                              {roadmap.title}
                            </h3>
                          </div>
                        </div>

                        {/* Description */}
                        {roadmap.description && (
                          <p className={`text-sm line-clamp-2 mb-3 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                            {roadmap.description}
                          </p>
                        )}

                        {/* Meta */}
                        <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'} mb-4`}>
                          <div>{roadmap.difficulty} • {roadmap.total_days} days</div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 mt-auto">
                          <button
                            onClick={() => handleEditPremiumRoadmap(roadmap)}
                            className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                              isDark
                                ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                            }`}
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                          
                          <button
                            onClick={() => handlePublishPremiumRoadmap(roadmap.id, roadmap.is_published)}
                            disabled={publishingPremiumRoadmap === roadmap.id}
                            className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                              roadmap.is_published
                                ? isDark
                                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 disabled:opacity-50'
                                  : 'bg-green-100 text-green-600 hover:bg-green-200 disabled:opacity-50'
                                : isDark
                                ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 disabled:opacity-50'
                                : 'bg-amber-100 text-amber-600 hover:bg-amber-200 disabled:opacity-50'
                            }`}
                          >
                            {publishingPremiumRoadmap === roadmap.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Globe className="w-4 h-4" />
                                {roadmap.is_published ? 'Unpublish' : 'Publish'}
                              </>
                            )}
                          </button>
                          
                          <button
                            onClick={() => handleDeletePremiumRoadmap(roadmap.id)}
                            disabled={deletingPremiumRoadmap === roadmap.id}
                            className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                              isDark
                                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-50'
                                : 'bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50'
                            }`}
                          >
                            {deletingPremiumRoadmap === roadmap.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'premium-roadmaps' && showPremiumRoadmapForm && (
            <div className="space-y-6">
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={() => {
                    setShowPremiumRoadmapForm(false);
                    setEditingPremiumRoadmap(null);
                  }}
                  className="p-2 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded-lg transition"
                >
                  <X className="w-5 h-5" />
                </button>
                <h2 className="text-2xl font-bold">{editingPremiumRoadmap ? 'Edit' : 'Create'} Premium Roadmap</h2>
              </div>
              <AdminRoadmapForm
                initialData={editingPremiumRoadmap || undefined}
                onSubmit={handlePremiumRoadmapSubmit}
                onCancel={() => {
                  setShowPremiumRoadmapForm(false);
                  setEditingPremiumRoadmap(null);
                }}
                isDark={isDark}
                isLoading={premiumRoadmapLoading}
              />
            </div>
          )}

          {tab === 'inbox' && (
            <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden h-[calc(100vh-250px)]">
              <AdminInbox theme={theme || 'dark'} />
            </div>
          )}

          {tab === 'customization' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Site Customization</h2>
                <button
                  onClick={saveCustomization}
                  disabled={customizationSaving}
                  className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 disabled:opacity-50 transition-all"
                >
                  <Save className="w-4 h-4" />
                  {customizationSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Site Information */}
                <div className="p-6 rounded-2xl border bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                  <h3 className="text-lg font-bold mb-4">Site Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Site Name</label>
                      <input
                        type="text"
                        value={customization.siteName}
                        onChange={(e) => setCustomization(prev => ({ ...prev, siteName: e.target.value }))}
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Site Description</label>
                      <textarea
                        value={customization.siteDescription}
                        onChange={(e) => setCustomization(prev => ({ ...prev, siteDescription: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Site Author</label>
                      <input
                        type="text"
                        value={customization.siteAuthor}
                        onChange={(e) => setCustomization(prev => ({ ...prev, siteAuthor: e.target.value }))}
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Site Email</label>
                      <input
                        type="email"
                        value={customization.siteEmail}
                        onChange={(e) => setCustomization(prev => ({ ...prev, siteEmail: e.target.value }))}
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Contact Phone</label>
                      <input
                        type="tel"
                        value={customization.contactPhone || ''}
                        onChange={(e) => setCustomization(prev => ({ ...prev, contactPhone: e.target.value }))}
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Theme Colors */}
                <div className="p-6 rounded-2xl border bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                  <h3 className="text-lg font-bold mb-4">Theme Colors</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Primary Color</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={customization.primaryColor}
                          onChange={(e) => setCustomization(prev => ({ ...prev, primaryColor: e.target.value }))}
                          className="w-16 h-10 rounded-lg border border-zinc-300 dark:border-zinc-700 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={customization.primaryColor}
                          onChange={(e) => setCustomization(prev => ({ ...prev, primaryColor: e.target.value }))}
                          className="flex-1 px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white text-sm font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Secondary Color</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={customization.secondaryColor}
                          onChange={(e) => setCustomization(prev => ({ ...prev, secondaryColor: e.target.value }))}
                          className="w-16 h-10 rounded-lg border border-zinc-300 dark:border-zinc-700 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={customization.secondaryColor}
                          onChange={(e) => setCustomization(prev => ({ ...prev, secondaryColor: e.target.value }))}
                          className="flex-1 px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white text-sm font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Accent Color</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={customization.accentColor}
                          onChange={(e) => setCustomization(prev => ({ ...prev, accentColor: e.target.value }))}
                          className="w-16 h-10 rounded-lg border border-zinc-300 dark:border-zinc-700 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={customization.accentColor}
                          onChange={(e) => setCustomization(prev => ({ ...prev, accentColor: e.target.value }))}
                          className="flex-1 px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white text-sm font-mono"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* SEO Information */}
                <div className="p-6 rounded-2xl border bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 md:col-span-2">
                  <h3 className="text-lg font-bold mb-4">SEO Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">SEO Title</label>
                      <input
                        type="text"
                        value={customization.seoTitle}
                        onChange={(e) => setCustomization(prev => ({ ...prev, seoTitle: e.target.value }))}
                        placeholder="Page title in search results (50-60 chars)"
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white"
                      />
                      <p className="text-xs text-zinc-500 mt-1">{customization.seoTitle.length} characters</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">SEO Description</label>
                      <textarea
                        value={customization.seoDescription}
                        onChange={(e) => setCustomization(prev => ({ ...prev, seoDescription: e.target.value }))}
                        placeholder="Meta description in search results (150-160 chars)"
                        rows={3}
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white"
                      />
                      <p className="text-xs text-zinc-500 mt-1">{customization.seoDescription.length} characters</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Keywords</label>
                      <textarea
                        value={customization.seoKeywords}
                        onChange={(e) => setCustomization(prev => ({ ...prev, seoKeywords: e.target.value }))}
                        placeholder="Comma-separated keywords"
                        rows={2}
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="p-6 rounded-2xl border bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 md:col-span-2">
                  <h3 className="text-lg font-bold mb-4">Social Links</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Twitter</label>
                      <input
                        type="url"
                        value={customization.socialLinks?.twitter || ''}
                        onChange={(e) => setCustomization(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, twitter: e.target.value } }))}
                        placeholder="https://twitter.com/yourhandle"
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">LinkedIn</label>
                      <input
                        type="url"
                        value={customization.socialLinks?.linkedin || ''}
                        onChange={(e) => setCustomization(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, linkedin: e.target.value } }))}
                        placeholder="https://linkedin.com/company/..."
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">GitHub</label>
                      <input
                        type="url"
                        value={customization.socialLinks?.github || ''}
                        onChange={(e) => setCustomization(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, github: e.target.value } }))}
                        placeholder="https://github.com/yourprofile"
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Discord</label>
                      <input
                        type="url"
                        value={customization.socialLinks?.discord || ''}
                        onChange={(e) => setCustomization(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, discord: e.target.value } }))}
                        placeholder="https://discord.gg/..."
                        className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
